import { View, Text, ScrollView, Pressable, StyleSheet, RefreshControl, Image } from 'react-native'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import Animated, { useSharedValue, useAnimatedStyle, withRepeat, withTiming, Easing } from 'react-native-reanimated'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { Zap, Smartphone, Wifi, FileText, ChevronDown, Check, Clock, TrendingDown, Plus, Info, ArrowRight, Search } from 'lucide-react-native'
import { BottomSheetModal, BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet'
import { apiClient } from '../../../src/api/client'
import { useAuth } from '../../../src/auth/AuthProvider'
import { useHuntStats } from '../../../src/hooks/useHuntStats'
import AcceptQuoteModal, { type AcceptableQuote } from '../../../src/components/AcceptQuoteModal'
import SharedQuoteCard, { type QuoteData, type Badge, BADGES, assignBadges, assignBadgesAndSelect, getBadge, getBestValueIndex, getFeatures, getProviderColor, getDisplayPrice, CATEGORY_CONFIG } from '../../../src/components/QuoteCard'
import { getEnergyBreakdown, calcEnergyTotal, DEFAULT_KWH_YEAR, type TariffData } from '../../../src/components/EnergyBreakdown'
import { computeQuoteSavings } from '../../../src/utils/savings'
import { colors, spacing, borderRadius, typography, shadows } from '../../../src/theme'
import { haptics } from '../../../src/utils/haptics'
import PressableScale from '../../../src/components/PressableScale'
import AnimatedItem from '../../../src/components/AnimatedItem'
import { SkeletonCard } from '../../../src/components/Skeleton'
import ContentContainer from '../../../src/components/ContentContainer'
import { events } from '../../../src/lib/posthog'
import { toast } from '../../../src/components/Toast'
import { appEvents } from '../../../src/lib/events'
import { useFeedback } from '../../../src/hooks/useFeedback'
import { useResponsive } from '../../../src/hooks/useResponsive'
import { useDataLoading } from '../../../src/hooks/useDataLoading'
import { getProviderLogo } from '../../../src/utils/providerLogos'
import { sentryCaptureException } from '../../../src/lib/sentry'

export { ScreenErrorBoundary as ErrorBoundary } from '../../../src/components/ScreenErrorBoundary'

type Category = 'energy' | 'mobile' | 'internet'

// Quote type — extends QuoteData with required status/created_at
interface Quote extends QuoteData {
  status: string
  created_at: string
}

// ─── Progress Pipeline ───────────────────────────

const PIPELINE_STEPS = [
  { key: 'sent', label: 'Sendt' },
  { key: 'waiting', label: 'Venter' },
  { key: 'quotes', label: 'Tilbud' },
  { key: 'accepted', label: 'Valgt' },
] as const

function PulsingDot({ size, color }: { size: number; color: string }) {
  const opacity = useSharedValue(1)
  useEffect(() => {
    opacity.value = withRepeat(
      withTiming(0.3, { duration: 900, easing: Easing.inOut(Easing.ease) }),
      -1,
      true
    )
  }, [])
  const animStyle = useAnimatedStyle(() => ({ opacity: opacity.value }))
  return (
    <Animated.View
      style={[
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color,
        },
        animStyle,
      ]}
    />
  )
}

interface ProgressPipelineProps {
  hunt: import('../../../src/hooks/useHuntStats').HuntView
  quotes: Quote[]
  acceptedCategories: Set<string>
  displayedQuoteCount?: number
}

function ProgressPipeline({ hunt, quotes, acceptedCategories, displayedQuoteCount = 0 }: ProgressPipelineProps) {
  // Determine which step we're at
  const hasResponses = hunt.respondedProviders > 0
  const totalQuoteCount = displayedQuoteCount > 0 ? displayedQuoteCount : hunt.providers.filter(p => p.has_quote).length
  const hasQuotes = totalQuoteCount > 0 ||
    quotes.some(q => hunt.quoteRequest.categories.includes(q.category) && q.status !== 'superseded' && q.status !== 'expired')
  const hasAccepted = hunt.quoteRequest.categories.some(cat => acceptedCategories.has(cat))

  // Step states: 'completed' | 'current' | 'future'
  type StepState = 'completed' | 'current' | 'future'
  const stepStates: StepState[] = (() => {
    if (hasAccepted) return ['completed', 'completed', 'completed', 'completed']
    if (hasQuotes) return ['completed', 'completed', 'completed', 'future']
    if (hasResponses) return ['completed', 'completed', 'future', 'future']
    return ['completed', 'current', 'future', 'future']
  })()

  const DOT_SIZE = 16
  const LINE_HEIGHT = 2

  return (
    <View style={pipelineStyles.container}>
      <View style={pipelineStyles.track}>
        {PIPELINE_STEPS.map((step, i) => {
          const state = stepStates[i]
          const isLast = i === PIPELINE_STEPS.length - 1
          return (
            <View key={step.key} style={[pipelineStyles.stepWrapper, isLast && pipelineStyles.lastStep]}>
              {/* Dot + connecting line row */}
              <View style={pipelineStyles.dotAndLine}>
                {/* Leading line for last step (connects from previous step) */}
                {isLast && (
                  <View
                    style={[
                      pipelineStyles.line,
                      { height: LINE_HEIGHT },
                      state === 'future'
                        ? { backgroundColor: '#cbd5e1' }
                        : { backgroundColor: colors.primary },
                    ]}
                  />
                )}
                {state === 'current' ? (
                  <View style={pipelineStyles.pulseOuter}>
                    <PulsingDot size={DOT_SIZE} color={colors.primary} />
                  </View>
                ) : (
                  <View
                    style={[
                      pipelineStyles.dot,
                      { width: DOT_SIZE, height: DOT_SIZE, borderRadius: DOT_SIZE / 2 },
                      state === 'completed'
                        ? { backgroundColor: colors.primary }
                        : { backgroundColor: 'transparent', borderWidth: 2, borderColor: '#cbd5e1' },
                    ]}
                  >
                    {state === 'completed' && (
                      <Check size={10} color="#fff" strokeWidth={3} />
                    )}
                  </View>
                )}
                {/* Trailing line (connects to next step) */}
                {!isLast && (
                  <View
                    style={[
                      pipelineStyles.line,
                      { height: LINE_HEIGHT },
                      stepStates[i + 1] === 'future' && state !== 'current'
                        ? { backgroundColor: '#cbd5e1' }
                        : state === 'completed'
                          ? { backgroundColor: colors.primary }
                          : { backgroundColor: '#cbd5e1' },
                    ]}
                  />
                )}
              </View>
              {/* Label + count */}
              <Text
                style={[
                  pipelineStyles.label,
                  state === 'completed' ? { color: colors.primary } :
                  state === 'current' ? { color: colors.primaryDark, fontWeight: '600' } :
                  { color: colors.text.muted },
                  isLast && pipelineStyles.lastStepLabel,
                ]}
                numberOfLines={2}
              >
                {step.label}
                {step.key === 'sent' && hunt.totalProviders > 0 ? `\n${hunt.totalProviders}` : ''}
                {step.key === 'waiting' && hunt.totalProviders > 0 ? `\n${hunt.totalProviders - hunt.respondedProviders}` : ''}
                {step.key === 'quotes' && totalQuoteCount > 0 ? `\n${totalQuoteCount}` : ''}
                {step.key === 'accepted' && hasAccepted ? `\n✓` : ''}
              </Text>
            </View>
          )
        })}
      </View>
      {/* Stats below */}
      <Text style={pipelineStyles.stats}>
        {hunt.respondedProviders}/{hunt.totalProviders} svar · {totalQuoteCount} tilbud
      </Text>
    </View>
  )
}

const pipelineStyles = StyleSheet.create({
  container: {
    marginTop: 4,
    paddingTop: 2,
  },
  track: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepWrapper: {
    flex: 1,
    alignItems: 'flex-start',
  },
  lastStep: {
    flex: 1,
    alignItems: 'flex-end',
  },
  dotAndLine: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  dot: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  pulseOuter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  line: {
    flex: 1,
    marginHorizontal: 2,
  },
  label: {
    ...typography.small,
    marginTop: 4,
    textAlign: 'left' as const,
  },
  lastStepLabel: {
    textAlign: 'right' as const,
  },
  stats: {
    ...typography.small,
    color: colors.text.muted,
    marginTop: 8,
  },
})

// ─── Screen ──────────────────────────────────────

export default function QuotesScreen() {
  const { user } = useAuth()
  const router = useRouter()
  const searchParams = useLocalSearchParams<{ category?: string }>()
  const { isDesktop } = useResponsive()
  const { loading, refreshing, loadError, setLoading, setRefreshing, setLoadError } = useDataLoading()
  const [expandedHuntId, setExpandedHuntId] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<Category>(
    (searchParams.category as Category) || 'energy'
  )

  // Sync activeCategory when navigating from dashboard with ?category= param
  useEffect(() => {
    if (searchParams.category && ['energy', 'mobile', 'internet'].includes(searchParams.category)) {
      setActiveCategory(searchParams.category as Category)
    }
  }, [searchParams.category])

  const [quotes, setQuotes] = useState<Quote[]>([])
  const [quoteRequests, setQuoteRequests] = useState<any[]>([])
  const [huntMessages, setHuntMessages] = useState<any[]>([])
  const [publicPlans, setPublicPlans] = useState<Record<string, any[]>>({ energy: [], mobile: [], internet: [] })
  const [currentProviders, setCurrentProviders] = useState<Record<string, any> | null>(null)
  const [userProfile, setUserProfile] = useState<any>(null)

  const [tariffs, setTariffs] = useState<TariffData | null>(null)

  const [acceptModalQuote, setAcceptModalQuote] = useState<AcceptableQuote | null>(null)
  // Latched quote: stays non-null while modal animates closed so AcceptQuoteModal
  // always mounts with a valid quote (fixes BottomSheetModal ref-before-present race)
  const latchedAcceptQuoteRef = useRef<AcceptableQuote | null>(null)
  if (acceptModalQuote) latchedAcceptQuoteRef.current = acceptModalQuote
  const latchedAcceptQuote = latchedAcceptQuoteRef.current
  const [cancelSwitchTarget, setCancelSwitchTarget] = useState<{ category: string; provider: string } | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelConfirming, setCancelConfirming] = useState(false)
  const [completingHuntId, setCompletingHuntId] = useState<string | null>(null)
  const [priceTooltipCat, setPriceTooltipCat] = useState<string | null>(null)
  const [showAwaitingProviders, setShowAwaitingProviders] = useState(false)
  const [huntPipelineExpanded, setHuntPipelineExpanded] = useState(false)
  const [completingLoading, setCompletingLoading] = useState(false)
  const [hasPaymentInfo, setHasPaymentInfo] = useState(false)
  const [existingPaymentReg, setExistingPaymentReg] = useState<string | null>(null)
  const [existingPaymentAccount, setExistingPaymentAccount] = useState<string | null>(null)
  const [acceptedCategories, setAcceptedCategoriesState] = useState<Set<string>>(new Set())
  const [acceptedProviders, setAcceptedProviders] = useState<Record<string, string>>({})
  const [billAverages, setBillAverages] = useState<Record<string, any>>({})
  const { openFeedback, shouldFireTrigger } = useFeedback()
  const feedbackTriggeredRef = useRef(false)
  const cancelSwitchSheetRef = useRef<BottomSheetModal>(null)
  const completeHuntSheetRef = useRef<BottomSheetModal>(null)

  useEffect(() => {
    if (cancelSwitchTarget) { cancelSwitchSheetRef.current?.present(); haptics.medium() }
    else cancelSwitchSheetRef.current?.dismiss()
  }, [cancelSwitchTarget])

  useEffect(() => {
    if (completingHuntId) { completeHuntSheetRef.current?.present(); haptics.light() }
    else completeHuntSheetRef.current?.dismiss()
  }, [completingHuntId])

  const renderSheetBackdrop = useCallback(
    (props: any) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} pressBehavior="close" />,
    []
  )

  const loadData = useCallback(async () => {
    setLoadError(false)
    try {
      const userId = user?.id || ''
      if (!userId) return

      // Step 1: Fetch core data
      const [quotesRes, requestsRes, inboxRes, userRes, billHistoryRes] = await Promise.all([
        apiClient.quotes.list(userId).catch(() => ({ data: [] })),
        apiClient.quotes.getRequests(userId).catch(() => ({ data: [] })),
        apiClient.inbox.list({ limit: '200' }).catch(() => ({ data: { messages: [] } })),
        apiClient.users.getMe(userId).catch(() => ({ data: null })),
        apiClient.contracts.billHistory().catch(() => ({ data: { averages: {} } })),
      ])

      setUserProfile(userRes.data || null)
      setCurrentProviders(userRes.data?.current_providers || null)
      setHasPaymentInfo(userRes.data?.has_payment_info === true)
      setExistingPaymentReg(userRes.data?.payment_reg_masked || null)
      setExistingPaymentAccount(userRes.data?.payment_account_masked || null)
      setBillAverages(billHistoryRes.data?.averages || {})
      const reqs = requestsRes.data || []
      const fetchedQuotes = quotesRes.data || []
      setQuotes(fetchedQuotes)
      setQuoteRequests(reqs)
      setHuntMessages((inboxRes.data?.messages || []).filter((m: any) => m.quote_request_id))

      // Pre-fill acceptedCategories from current_providers + accepted quotes (matching Next.js)
      if (userRes.data) {
        const cp = userRes.data?.current_providers
        const alreadyAccepted = new Set<string>()
        const providerMap: Record<string, string> = {}
        if (cp) {
          for (const [cat, info] of Object.entries(cp)) {
            if ((info as any)?.status === 'pending_switch') {
              alreadyAccepted.add(cat)
              if ((info as any)?.provider_name) providerMap[cat] = (info as any).provider_name
            }
          }
        }
        for (const q of fetchedQuotes) {
          if (q.status === 'accepted') {
            alreadyAccepted.add(q.category)
            if (!providerMap[q.category]) providerMap[q.category] = q.provider
          }
        }
        if (alreadyAccepted.size > 0) {
          setAcceptedCategoriesState(prev => {
            const merged = new Set(prev)
            alreadyAccepted.forEach(c => merged.add(c))
            return merged
          })
        }
        if (Object.keys(providerMap).length > 0) {
          setAcceptedProviders(prev => ({ ...prev, ...providerMap }))
        }
      }

      // Step 2: Extract user preferences from active hunt to filter public plans
      const activeReq = reqs.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
      const userData = activeReq?.user_data || {}
      const mobilePlan = Array.isArray(userData.mobile) ? userData.mobile[0] : userData.mobile
      const internetPlan = Array.isArray(userData.internet) ? userData.internet[0] : userData.internet
      const minGb = Number(mobilePlan?.desired_gb_dk) || undefined
      const internetNeeds = internetPlan?.needs as string | undefined
      const minSpeed = internetNeeds === 'premium' ? 300 : internetNeeds === 'standard' ? 100 : undefined

      const dedupFilter: Record<string, string> = { unique_per_provider: 'true' }
      const mobileFilter = { ...dedupFilter, ...(minGb ? { min_gb: String(minGb) } : {}) }
      const internetFilter = { ...dedupFilter, ...(minSpeed ? { min_speed: String(minSpeed) } : {}) }

      // Step 3: Fetch public plans with user-appropriate filters
      const [ePub, mPub, iPub] = await Promise.all([
        apiClient.publicPrices.cheapest('energy', 20, dedupFilter).catch(() => ({ data: { plans: [] } })),
        apiClient.publicPrices.cheapest('mobile', 20, mobileFilter).catch(() => ({ data: [] })),
        apiClient.publicPrices.cheapest('internet', 20, internetFilter).catch(() => ({ data: [] })),
      ])

      if (ePub.data?.tariffs) setTariffs(ePub.data.tariffs)
      const plans: Record<string, any[]> = {
        energy: ePub.data?.plans || ePub.data || [],
        mobile: mPub.data || [],
        internet: iPub.data || [],
      }
      setPublicPlans(plans)

      // Feedback trigger: first_quote_view after 30s (post_onboarding fires from onboarding directly)
      if (!feedbackTriggeredRef.current) {
        feedbackTriggeredRef.current = true
        const hasQuotes = ((quotesRes.data || []).length > 0 || Object.keys(plans).length > 0)
        if (hasQuotes && shouldFireTrigger('first_quote_view')) {
          setTimeout(() => openFeedback('first_quote_view', 'Mangler du noget i overblikket?'), 30_000)
        }
      }
    } catch (error: any) {
      sentryCaptureException(error, { screen: 'quotes' })
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    events.screenViewed('quotes')
    if (user?.id) loadData()
  }, [user?.id])

  // Re-fetch when inbox changes (quote accepted, new provider response)
  useEffect(() => {
    const handler = () => { loadData() }
    const unsub = appEvents.on('inbox-updated', handler)
    return unsub
  }, [loadData])

  const onRefresh = useCallback(async () => {
    haptics.light()
    setRefreshing(true)
    await loadData()
    setRefreshing(false)
  }, [loadData])

  // Hunt stats — public offers count as responses (same as personalized)
  const offeredProviders = useMemo(() => {
    const offered: Record<string, Set<string>> = {}
    for (const q of quotes) {
      if (!offered[q.category]) offered[q.category] = new Set()
      offered[q.category].add(q.provider)
    }
    for (const [cat, plans] of Object.entries(publicPlans)) {
      for (const p of plans) {
        if (!offered[cat]) offered[cat] = new Set()
        offered[cat].add(p.provider)
      }
    }
    return offered
  }, [quotes, publicPlans])

  const { hunts } = useHuntStats(quoteRequests, huntMessages, offeredProviders)

  // Current agreements from user_data (matches Next.js extractCurrentAgreements)
  // Search active requests first, then any request with user_data (for accepted categories)
  const currentAgreements = useMemo(() => {
    const active = quoteRequests.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
      || quoteRequests.find((r: any) => r.user_data)
    if (!active?.user_data) return {} as Record<string, { provider: string; price: number; priceConfirmed: boolean; details: string; yearlyKwh?: number; desiredGbDk?: number; internetNeeds?: string }>
    const result: Record<string, { provider: string; price: number; priceConfirmed: boolean; details: string; yearlyKwh?: number; desiredGbDk?: number; internetNeeds?: string }> = {}
    for (const [cat, data] of Object.entries(active.user_data as Record<string, any>)) {
      const plans = Array.isArray(data) ? data : [data]
      const plan = plans[0]
      if (!plan || typeof plan !== 'object') continue
      const provider = plan.provider || ''
      const billPrice = billAverages[cat]?.confirmed_current_price?.monthly_price
      const confirmedPrice = plan.confirmed_price ? Number(plan.confirmed_price) : undefined
      const price = billPrice ?? confirmedPrice ?? (Number(plan.price) || 0)
      const priceConfirmed = billPrice != null || !!confirmedPrice
      let details = ''
      let yearlyKwh: number | undefined
      if (cat === 'energy') {
        yearlyKwh = Number(plan.yearly_kwh) || undefined
        details = yearlyKwh ? `${yearlyKwh.toLocaleString('da-DK')} kWh/år` : ''
      } else if (cat === 'mobile') {
        details = plan.desired_gb_dk ? `${plan.desired_gb_dk} GB` : ''
      } else if (cat === 'internet') {
        details = plan.needs === 'premium' ? 'Premium' : plan.needs === 'basis' ? 'Basis' : 'Standard'
      }
      const desiredGbDk = cat === 'mobile' ? (Number(plan.desired_gb_dk) || undefined) : undefined
      const internetNeeds = cat === 'internet' ? (plan.needs as string || undefined) : undefined
      if (provider && price > 0) result[cat] = { provider, price, priceConfirmed, details, yearlyKwh, desiredGbDk, internetNeeds }
    }
    return result
  }, [quoteRequests, billAverages])

  // Savings calculation — uses shared computeQuoteSavings (same formula as Dashboard + Market)
  const computeSavings = useCallback((quote: Quote): number | undefined => {
    const agreement = currentAgreements[quote.category]
    if (!(agreement && agreement.price > 0)) return undefined
    const yearlyKwh = quote.category === 'energy' ? currentAgreements.energy?.yearlyKwh : undefined
    return computeQuoteSavings(agreement.price, quote, tariffs, yearlyKwh)
  }, [currentAgreements, tariffs])

  // activeCategories — which categories have an active hunt (matching Next.js)
  const activeCategories = useMemo(() => {
    const cats = new Set<string>()
    quoteRequests.filter((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
      .forEach((r: any) => r.categories?.forEach((c: string) => cats.add(c)))
    return cats
  }, [quoteRequests])
  const allCategoriesActive = activeCategories.size >= 3

  /** Convert a PublicPlan to a Quote-compatible object (matches Next.js publicPlanToQuote).
   *  For energy: recalculates full monthly price using user's actual kWh + real tariff data. */
  const publicPlanToQuote = useCallback((plan: any, category: string, idx: number, yearlyKwh?: number, catTariffs?: TariffData | null): Quote => {
    const details: Record<string, any> = {}
    let price: number

    if (category === 'energy' && catTariffs) {
      const kwh = Number(yearlyKwh) || DEFAULT_KWH_YEAR
      const monthlyKwh = kwh / 12
      const sub = Number(plan.monthly_subscription) || 0
      const surchargeOre = Number(plan.kwh_surcharge) || 0
      const kwhCost = (surchargeOre / 100) * monthlyKwh
      const spotCost = (catTariffs.spot_price_ore / 100) * monthlyKwh
      const nettarifCost = (catTariffs.nettarif_ore / 100) * monthlyKwh
      const nettarifAbo = catTariffs.nettarif_detail.abonnement_kr_md
      const elafgiftCost = (catTariffs.elafgift_ore / 100) * monthlyKwh
      const systemtarifCost = (catTariffs.systemtarif_ore / 100) * monthlyKwh
      const transmissionstarifCost = (catTariffs.transmissionstarif_ore / 100) * monthlyKwh

      const breakdown: Record<string, number> = {
        subscription: sub, kwh_cost: kwhCost, spot_price: spotCost,
        nettarif: nettarifCost, nettarif_abonnement: nettarifAbo,
        elafgift: elafgiftCost, systemtarif: systemtarifCost,
        transmissionstarif: transmissionstarifCost,
      }
      price = calcEnergyTotal(breakdown, catTariffs, kwh)
      details.energy_breakdown = breakdown
      details.monthly_kwh = Math.round(monthlyKwh)
      details.yearly_kwh = kwh
      if (plan.kwh_surcharge != null) details.kwh_surcharge = plan.kwh_surcharge
      if (plan.monthly_subscription != null) details.monthly_subscription = plan.monthly_subscription
      if (plan.creation_fee_kr) details.creation_fee_kr = plan.creation_fee_kr
      if (plan.cancellation_fee_kr) details.cancellation_fee_kr = plan.cancellation_fee_kr
      if (plan.price_type) details.price_type = plan.price_type
      if (plan.payment_type) details.payment_type = plan.payment_type
    } else {
      price = plan.monthly_price ?? plan.monthly_subscription ?? 0
    }

    if (plan.green_energy) details.green_energy = true
    if (plan.is_spot_based) details.is_spot_based = true
    // null = unlimited; preserve null explicitly so QuoteCard shows "Fri data"
    if (plan.data_dk_gb !== undefined) details.gb_dk = plan.data_dk_gb ?? null
    if (plan.data_eu_gb) details.data_eu_gb = plan.data_eu_gb
    if (plan.eu_data) details.eu_data = true
    if (plan.network) details.network = plan.network
    if (plan.speed) details.speed = plan.speed
    if (plan.calls_sms) details.calls_sms = plan.calls_sms
    if (plan.hotspot_tethering) details.hotspot_tethering = plan.hotspot_tethering
    if (plan.roaming_terms) details.roaming_terms = plan.roaming_terms
    if (plan.creation_fee != null) details.creation_fee = plan.creation_fee
    if (plan.special_features) details.special_features = plan.special_features
    if (plan.download_mbit) details.download_mbit = plan.download_mbit
    if (plan.upload_mbit) details.upload_mbit = plan.upload_mbit
    if (plan.technology) details.technology = plan.technology
    if (plan.router_included != null) details.router_included = plan.router_included
    if (plan.router_model) details.router_model = plan.router_model
    if (plan.cancellation_notice) details.cancellation_notice = plan.cancellation_notice
    if (plan.coverage_area) details.coverage_area = plan.coverage_area
    if (plan.intro_offer) details.intro_offer = plan.intro_offer
    if (plan.intro_price != null) details.intro_price = plan.intro_price
    if (plan.intro_period) details.intro_period = plan.intro_period
    if (plan.contract_length != null) {
      const len = plan.contract_length
      if (len === 0 || len === 'Ingen binding' || len === '0 mdr' || len === '0 md.') details.contract_length = 'Ingen binding'
      else if (typeof len === 'number') details.contract_length = `${len} md.`
      else details.contract_length = String(len)
    }
    details.source = plan.source

    return {
      id: `public-${category}-${idx}`,
      provider: plan.provider,
      product_name: plan.product_name,
      category,
      monthly_price: price,
      details,
      status: 'public',
      created_at: new Date().toISOString(),
    }
  }, [])

  // Group quotes by category (all categories stacked, like Next.js)
  const quotesByCategory = useMemo(() => {
    const catOrder: Category[] = ['energy', 'mobile', 'internet']
    const groups: { category: Category; quotes: Quote[]; badgeMap: Map<string, Badge>; displayQuotes: QuoteData[]; currentAgreement?: typeof currentAgreements['energy'] }[] = []

    for (const cat of catOrder) {
      if (acceptedCategories.has(cat)) continue

      // Personalized quotes first
      const personalQuotes = quotes.filter(q => q.category === cat && q.status !== 'superseded' && q.status !== 'expired')
      // Convert public plans using publicPlanToQuote with tariff recalc for energy
      const energyKwh = currentAgreements.energy?.yearlyKwh
      const publicQuotes = (publicPlans[cat] || []).map((p: any, i: number) =>
        publicPlanToQuote(p, cat, i, cat === 'energy' ? energyKwh : undefined, tariffs)
      )
      // Merge: personalized first, then public (dedup by provider, exclude zero-price)
      const personalProviders = new Set(personalQuotes.map(q => q.provider.toLowerCase().trim()))
      const dedupedPublic = publicQuotes.filter(q =>
        !personalProviders.has(q.provider.toLowerCase().trim()) &&
        Number(q.monthly_price) > 0
      )
      const mergedQuotes = [...personalQuotes, ...dedupedPublic]

      // Add savings calculation — always recompute using shared formula
      const agreement = currentAgreements[cat]
      const yearlyKwh = cat === 'energy' ? energyKwh : undefined
      const allQuotes = mergedQuotes.map(q => {
        if (!(agreement && agreement.price > 0)) return q
        const savings = computeQuoteSavings(agreement.price, q, tariffs, yearlyKwh)
        if (savings != null) {
          return { ...q, estimated_annual_savings: savings }
        }
        return { ...q, estimated_annual_savings: undefined }
      })

      if (allQuotes.length > 0) {
        // Sort by effective price (matching Next.js — energy uses calcEnergyTotal)
        const sorted = [...allQuotes].sort((a, b) =>
          getDisplayPrice(a, tariffs, agreement?.yearlyKwh) - getDisplayPrice(b, tariffs, agreement?.yearlyKwh)
        )
        const nonAccepted = sorted.filter(q => q.status !== 'accepted')
        // Badge assignment across ALL quotes — promotes winners into displayed top 5
        const { badges, displayQuotes } = assignBadgesAndSelect(nonAccepted, cat)
        groups.push({ category: cat, quotes: sorted, badgeMap: badges, displayQuotes, currentAgreement: agreement })
      }
    }
    return groups
  }, [quotes, publicPlans, acceptedCategories, currentAgreements, tariffs, publicPlanToQuote])

  // Best saving per non-accepted category — excludes categories already switched
  const totalSavings = useMemo(() => {
    return quotesByCategory
      .filter(({ category }) => !acceptedCategories.has(category))
      .reduce((sum, { quotes: qs }) => {
        const best = Math.max(0, ...qs.map(q => Number(q.estimated_annual_savings) || 0))
        return sum + best
      }, 0)
  }, [quotesByCategory, acceptedCategories])

  // Build accepted display list: all categories in acceptedCategories state
  // matching Next.js which iterates acceptedCategories and looks up providers/quotes
  const allAcceptedDisplay = useMemo(() => {
    return Array.from(acceptedCategories).map(cat => {
      const provider = acceptedProviders[cat]
      const acceptedQuote = quotes.find(q => q.status === 'accepted' && q.category === cat)
      const monthlyPrice = acceptedQuote ? Math.round(Number(acceptedQuote.monthly_price)) : null
      const yearlyKwh = cat === 'energy' ? currentAgreements.energy?.yearlyKwh : undefined
      let savings = acceptedQuote
        ? (computeQuoteSavings(currentAgreements[cat]?.price || 0, acceptedQuote, tariffs, yearlyKwh) || 0)
        : 0
      const oldProvider = currentAgreements[cat]?.provider || null
      const oldPrice = currentAgreements[cat]?.price ? Math.round(currentAgreements[cat].price) : null
      return { category: cat, provider, monthlyPrice, savings, oldProvider, oldPrice }
    })
  }, [acceptedCategories, acceptedProviders, quotes, currentAgreements])

  // Accept handlers
  const openAcceptModal = useCallback((quote: Quote) => {
    setAcceptModalQuote({
      ...quote,
      estimated_annual_savings: quote.estimated_annual_savings || computeSavings(quote),
    })
  }, [computeSavings])

  const handleAccept = useCallback(async (data: any) => {
    if (!acceptModalQuote) return
    const quote = acceptModalQuote
    const isPublicPlan = quote.id.toString().startsWith('public-')

    try {
      const payload = isPublicPlan
        ? {
            ...(data || {}),
            public_plan: true,
            category: quote.category,
            provider: quote.provider,
            product_name: quote.product_name,
            monthly_price: quote.monthly_price,
            estimated_annual_savings: quote.estimated_annual_savings,
            details: quote.details,
          }
        : data

      const res = await apiClient.quotes.accept(quote.id, payload, isPublicPlan ? { _skipAuthRedirect: true } as any : undefined)
      if (!isPublicPlan) {
        setHasPaymentInfo(true)
        events.quoteAccepted(String(quote.id), quote.provider, quote.category)
      }
      setAcceptedCategoriesState(prev => new Set(prev).add(quote.category))
      setAcceptedProviders(prev => ({ ...prev, [quote.category]: quote.provider }))
      if (isPublicPlan) {
        setQuotes(prev => [...prev, { ...quote, status: 'accepted' } as Quote])
      } else {
        setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'accepted' } : q))
      }
      toast.success(`Vi kontakter ${quote.provider} — du hører fra os!`)
      appEvents.emitDebounced('inbox-updated')
      setAcceptModalQuote(null)
    } catch (err: any) {
      sentryCaptureException(err, { action: 'accept_quote' })
      toast.error('Kunne ikke acceptere tilbuddet — vi er notificeret. Prøv igen.')
      throw err
    }
  }, [acceptModalQuote])

  // Cancel switch
  const handleCancelSwitch = useCallback((category: string, provider: string) => {
    setCancelSwitchTarget({ category, provider })
  }, [])

  const executeCancelSwitch = useCallback(async () => {
    if (!cancelSwitchTarget) return
    setCancelLoading(true)
    try {
      const res = await apiClient.quotes.cancelSwitch(cancelSwitchTarget.category)
      toast.success(res.data?.email_sent ? 'Skiftet er annulleret — vi har sendt en annullering' : 'Skiftet er annulleret')
      setCancelSwitchTarget(null)
      setCancelConfirming(false)
      // Remove from accepted and reload (matching Next.js)
      setAcceptedCategoriesState(prev => {
        const next = new Set(prev)
        next.delete(cancelSwitchTarget.category)
        return next
      })
      setAcceptedProviders(prev => {
        const next = { ...prev }
        delete next[cancelSwitchTarget.category]
        return next
      })
      appEvents.emitDebounced('inbox-updated')
      await loadData()
    } catch (err: any) {
      sentryCaptureException(err, { action: 'cancel_switch' })
      toast.error('Kunne ikke annullere skiftet — vi er notificeret. Prøv igen.')
    } finally {
      setCancelLoading(false)
    }
  }, [cancelSwitchTarget, loadData])

  // Complete hunt
  const confirmCompleteHunt = useCallback(async () => {
    if (!completingHuntId) return
    setCompletingLoading(true)
    try {
      await apiClient.quotes.completeRequest(completingHuntId)
      toast.success('Jagten er afsluttet!')
      setCompletingHuntId(null)
      await loadData()
    } catch (err: any) {
      sentryCaptureException(err, { action: 'complete_hunt' })
      toast.error('Kunne ikke afslutte jagten — vi er notificeret. Prøv igen.')
    } finally {
      setCompletingLoading(false)
    }
  }, [completingHuntId, loadData])

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={[]}>
        <View style={styles.header}>
          <Text style={styles.title}>Tilbud</Text>
        </View>
        <View style={{ padding: spacing.md, gap: spacing.sm }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Dine Tilbud</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
          {!allCategoriesActive && (
            <PressableScale
              accessibilityRole="button"
              accessibilityLabel="Start ny jagt"
              style={styles.startHuntBtn}
              onPress={() => router.push('/onboarding' as any)}
              haptic="light"
            >
              <Plus size={14} color={colors.text.inverse} />
              <Text style={styles.startHuntBtnText}>Start ny jagt</Text>
            </PressableScale>
          )}
        </View>
      </View>

      {/* Error banner */}
      {loadError && (
        <View style={styles.errorBanner}>
          <Info size={16} color="#92400e" />
          <Text style={styles.errorBannerText}>Noget gik galt — vores team er notificeret.</Text>
          <Pressable accessibilityRole="button" accessibilityLabel="Prøv igen" onPress={() => { setLoading(true); loadData() }}>
            <Text style={styles.errorBannerRetry}>Prøv igen</Text>
          </Pressable>
        </View>
      )}

      <ScrollView
        contentContainerStyle={{ padding: spacing.md, gap: spacing.md, paddingBottom: 100 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
        onScrollBeginDrag={() => setPriceTooltipCat(null)}
      >
        {/* Savings summary */}
        {totalSavings > 0 && (
          <AnimatedItem index={0}>
            <View style={styles.savingsSummary}>
              <TrendingDown size={16} color={colors.success} />
              <Text style={styles.savingsSummaryAmount}>{Math.round(totalSavings).toLocaleString('da-DK')} kr/år</Text>
              <Text style={styles.savingsSummaryLabel}>potentiel besparelse</Text>
            </View>
          </AnimatedItem>
        )}

        {/* Hunt progress — compact by default */}
        {hunts.length > 0 && (
          <AnimatedItem index={1}>
          <View style={styles.huntBanner}>
            {hunts.map((hunt, huntIdx) => {
              const isExpanded = expandedHuntId === hunt.quoteRequest.id
              const totalQuotes = hunt.quoteRequest.categories.reduce((sum: number, cat: string) => {
                const group = quotesByCategory.find(g => g.category === cat)
                return sum + (group ? group.quotes.length : 0)
              }, 0)
              const catLabels = hunt.quoteRequest.categories?.map((c: string) =>
                CATEGORY_CONFIG.find(cc => cc.id === c)?.label
              ).join(', ')
              return (
                <View key={hunt.quoteRequest.id} style={huntIdx > 0 ? { marginTop: spacing.sm, borderTopWidth: 1, borderTopColor: '#c7d2fe', paddingTop: spacing.sm } : undefined}>
                  <PressableScale
                    style={styles.huntBannerRow}
                    onPress={() => {
                      if (isExpanded) { setExpandedHuntId(null); setHuntPipelineExpanded(false) }
                      else setExpandedHuntId(hunt.quoteRequest.id)
                    }}
                    haptic="light"
                  >
                    <View style={{ flex: 1 }}>
                      {/* Compact summary — always visible, 2 rows for mobile breathing room */}
                      {/* Row 1: categories + chevron */}
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm }}>
                        <Text style={styles.huntBannerText} numberOfLines={1}>{catLabels}</Text>
                        <ChevronDown
                          size={14}
                          color={colors.text.muted}
                          style={{ transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }}
                        />
                      </View>
                      {/* Row 2: stats + unread badge */}
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, marginTop: 2 }}>
                        <Text style={styles.huntBannerStats} numberOfLines={1}>
                          {hunt.respondedProviders}/{hunt.totalProviders} svar · {totalQuotes} tilbud
                        </Text>
                        {hunt.unreadCount > 0 && (
                          <View style={styles.unreadBadge}>
                            <Text style={styles.unreadBadgeText}>{hunt.unreadCount} nye</Text>
                          </View>
                        )}
                      </View>
                      {/* Pipeline stepper — only when expanded */}
                      {isExpanded && (
                        <ProgressPipeline
                          hunt={hunt}
                          quotes={quotes}
                          acceptedCategories={acceptedCategories}
                          displayedQuoteCount={totalQuotes}
                        />
                      )}
                    </View>
                  </PressableScale>
                  {isExpanded && hunt.providers && (() => {
                    const respondedProviders = hunt.providers.filter((p: any) => p.has_inbound || p.has_quote)
                    const awaitingProviders = hunt.providers.filter((p: any) => !p.has_inbound && !p.has_quote)
                    return (
                    <View style={styles.huntProviderList}>
                      {respondedProviders.map((provider: any) => (
                        <View key={provider.provider_name} style={[styles.huntProviderRow, provider.has_unread && { backgroundColor: colors.primarySubtle }]}>
                          {getProviderLogo(provider.provider_name) ? (
                            <Image source={getProviderLogo(provider.provider_name)!} style={styles.huntProviderLogo} />
                          ) : (
                            <View style={[styles.huntProviderIcon, { backgroundColor: colors.successMuted }]}>
                              <Text style={[styles.huntProviderInitial, { color: colors.successDark }]}>
                                {provider.provider_name.charAt(0)}
                              </Text>
                            </View>
                          )}
                          <Text style={[styles.huntProviderName, provider.has_unread && { fontWeight: '600' }]} numberOfLines={1}>
                            {provider.provider_name}
                          </Text>
                          {provider.has_unread && <View style={styles.unreadDot} />}
                          <View style={[
                            styles.providerStatusBadge,
                            provider.status_label === 'Tilbud modtaget' ? { backgroundColor: colors.successMuted } :
                            { backgroundColor: colors.primaryLight },
                          ]}>
                            <Text style={[
                              styles.providerStatusText,
                              provider.status_label === 'Tilbud modtaget' ? { color: colors.successDark } :
                              { color: colors.primaryDark },
                            ]}>{provider.status_label}</Text>
                          </View>
                        </View>
                      ))}
                      {awaitingProviders.length > 0 && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={showAwaitingProviders ? 'Skjul afventende' : `Vis ${awaitingProviders.length} afventende`}
                          style={styles.showAwaitingBtn}
                          onPress={() => setShowAwaitingProviders(!showAwaitingProviders)}
                        >
                          <Text style={styles.showAwaitingText}>
                            {showAwaitingProviders ? 'Skjul afventende' : `${awaitingProviders.length} afventer svar`}
                          </Text>
                          <ChevronDown size={12} color={colors.text.muted} style={{ transform: [{ rotate: showAwaitingProviders ? '180deg' : '0deg' }] }} />
                        </Pressable>
                      )}
                      {showAwaitingProviders && awaitingProviders.map((provider: any) => (
                        <View key={provider.provider_name} style={[styles.huntProviderRow, { opacity: 0.6 }]}>
                          {getProviderLogo(provider.provider_name) ? (
                            <Image source={getProviderLogo(provider.provider_name)!} style={[styles.huntProviderLogo, { opacity: 0.6 }]} />
                          ) : (
                            <View style={[styles.huntProviderIcon, { backgroundColor: colors.surface }]}>
                              <Text style={[styles.huntProviderInitial, { color: colors.text.muted }]}>
                                {provider.provider_name.charAt(0)}
                              </Text>
                            </View>
                          )}
                          <Text style={styles.huntProviderName} numberOfLines={1}>
                            {provider.provider_name}
                          </Text>
                          <View style={[styles.providerStatusBadge, { backgroundColor: colors.surface }]}>
                            <Text style={[styles.providerStatusText, { color: colors.text.muted }]}>Afventer svar</Text>
                          </View>
                        </View>
                      ))}
                      {['pending', 'pending_approval', 'sent'].includes(hunt.quoteRequest.status) && (
                        <View style={styles.completeHuntFooter}>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel="Afslut jagt"
                            style={styles.completeHuntBtn}
                            onPress={() => setCompletingHuntId(hunt.quoteRequest.id)}
                          >
                            <Text style={styles.completeHuntText}>Afslut jagt</Text>
                          </Pressable>
                        </View>
                      )}
                    </View>
                  )})()}
                </View>
              )
            })}
          </View>
          </AnimatedItem>
        )}

        {/* Category tab bar */}
        {quotesByCategory.length > 0 && (
          <View style={styles.categoryTabBar}>
            {CATEGORY_CONFIG.map(cat => {
              const isActive = activeCategory === cat.id
              const CatIcon = cat.id === 'energy' ? Zap : cat.id === 'mobile' ? Smartphone : Wifi
              const catQuotes = quotesByCategory.find(g => g.category === cat.id)
              const count = catQuotes ? catQuotes.quotes.filter(q => q.status !== 'accepted').length : 0
              const hasAccepted = acceptedCategories.has(cat.id)
              return (
                <PressableScale
                  key={cat.id}
                  accessibilityRole="tab"
                  accessibilityLabel={`${cat.label}${count > 0 ? `, ${count} tilbud` : ''}${hasAccepted ? ', skifte i gang' : ''}`}
                  accessibilityState={{ selected: isActive }}
                  style={[
                    styles.categoryTab,
                    isActive && { backgroundColor: colors.primary, borderColor: colors.primary },
                  ]}
                  onPress={() => setActiveCategory(cat.id as Category)}
                  haptic="light"
                >
                  <CatIcon size={14} color={isActive ? colors.text.inverse : cat.color} />
                  <Text style={[styles.categoryTabText, isActive && { color: colors.text.inverse }]}>
                    {cat.label}
                  </Text>
                  {count > 0 && (
                    <View style={[styles.categoryTabBadge, isActive && { backgroundColor: 'rgba(255,255,255,0.25)' }]}>
                      <Text style={[styles.categoryTabBadgeText, isActive && { color: colors.text.inverse }]}>{count}</Text>
                    </View>
                  )}
                  {hasAccepted && !count && (
                    <Check size={12} color={isActive ? colors.text.inverse : colors.successDark} />
                  )}
                </PressableScale>
              )
            })}
          </View>
        )}

        {/* Accepted categories — Før → Efter comparison */}
        {allAcceptedDisplay.filter(a => a.category === activeCategory).map(({ category, provider, monthlyPrice, savings, oldProvider, oldPrice }, acceptIdx) => {
          const catConfig = CATEGORY_CONFIG.find(c => c.id === category)
          return (
            <AnimatedItem key={`accepted-${category}`} index={acceptIdx + 2}>
            <View style={styles.switchCompareCard}>
              {/* Header */}
              <View style={styles.switchCompareHeader}>
                <Text style={styles.switchCompareTitle}>{catConfig?.label} — Skifte i gang</Text>
                <PressableScale
                  accessibilityRole="button"
                  accessibilityLabel={`Se status for skifte til ${provider}`}
                  style={styles.afventerStatusBadge}
                  haptic="light"
                  onPress={() => handleCancelSwitch(category, provider || '')}
                >
                  <Text style={styles.afventerStatusText}>Under behandling</Text>
                  <ChevronDown size={10} color={colors.warningDark} />
                </PressableScale>
              </View>

              {/* Før → Efter */}
              <View style={styles.switchCompareRow}>
                {/* Før */}
                <View style={styles.switchCompareCol}>
                  <Text style={styles.switchCompareLabel}>FØR</Text>
                  {oldProvider ? (
                    <>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        {getProviderLogo(oldProvider) ? (
                          <Image source={getProviderLogo(oldProvider)!} style={{ width: 24, height: 24, borderRadius: 6 }} />
                        ) : (
                          <View style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: colors.text.muted }}>{oldProvider.charAt(0)}</Text>
                          </View>
                        )}
                        <Text style={styles.switchCompareProvider} numberOfLines={1}>{oldProvider}</Text>
                      </View>
                      {oldPrice ? (
                        <Text style={styles.switchCompareOldPrice}>{oldPrice} kr/md</Text>
                      ) : (
                        <Text style={{ fontSize: 12, color: colors.text.muted, marginTop: 2 }}>Afventer pris</Text>
                      )}
                    </>
                  ) : (
                    <Text style={[styles.switchCompareProvider, { marginTop: 4, color: colors.text.muted }]}>Ukendt</Text>
                  )}
                </View>

                {/* Arrow */}
                <View style={styles.switchCompareArrow}>
                  <ArrowRight size={16} color={colors.primary} />
                </View>

                {/* Efter */}
                <View style={styles.switchCompareCol}>
                  <Text style={[styles.switchCompareLabel, { color: colors.successDark }]}>EFTER</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    {getProviderLogo(provider || '') ? (
                      <Image source={getProviderLogo(provider || '')!} style={{ width: 24, height: 24, borderRadius: 6 }} />
                    ) : (
                      <View style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: colors.successMuted, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: colors.successDark }}>{(provider || '?').charAt(0)}</Text>
                      </View>
                    )}
                    <Text style={[styles.switchCompareProvider, { color: colors.successDark }]} numberOfLines={1}>{provider}</Text>
                  </View>
                  {monthlyPrice ? (
                    <Text style={styles.switchCompareNewPrice}>{monthlyPrice} kr/md</Text>
                  ) : (
                    <Text style={{ fontSize: 12, color: colors.text.muted, marginTop: 2 }}>Afventer pris</Text>
                  )}
                </View>
              </View>

              {/* Savings + status footer */}
              <View style={styles.switchCompareFooter}>
                {savings > 0 && (
                  <View style={styles.afventerSavingBadge}>
                    <TrendingDown size={12} color={colors.successDark} />
                    <Text style={styles.afventerSavingText}>Sparer {savings.toLocaleString('da-DK')} kr/år</Text>
                  </View>
                )}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1 }}>
                  <Clock size={12} color={colors.text.muted} />
                  <Text style={{ fontSize: 11, color: colors.text.muted, flex: 1 }}>
                    Fuldmagt sendt — du behøver ikke foretage dig noget
                  </Text>
                </View>
              </View>
            </View>
            </AnimatedItem>
          )
        })}

        {/* Per-category section — filtered by active tab */}
        {quotesByCategory.length > 0 ? (
          quotesByCategory.filter(g => g.category === activeCategory).map(({ category, quotes: catQuotes, badgeMap, displayQuotes, currentAgreement }, catIdx) => {
            const catConfig = CATEGORY_CONFIG.find(c => c.id === category)!
            const CatIcon = category === 'energy' ? Zap : category === 'mobile' ? Smartphone : Wifi
            const nonAccepted = catQuotes.filter(q => q.status !== 'accepted')
            const isEnergy = category === 'energy'
            const hasAnyBetterOffer = catQuotes.some(q => (Number(q.estimated_annual_savings) || 0) > 0)
            const staggerBase = allAcceptedDisplay.length + 2 + catIdx * 4

            return (
              <AnimatedItem key={category} index={staggerBase}>
              <View style={{ gap: spacing.sm }}>
                {/* Category header */}
                <View style={styles.categorySectionHeader}>
                  <View style={[styles.categorySectionIcon, { backgroundColor: catConfig.bg }]}>
                    <CatIcon size={16} color={catConfig.color} />
                  </View>
                  <Text style={styles.categorySectionTitle}>{catConfig.label}</Text>
                  <Text style={styles.categorySectionCount}>Viser {displayQuotes.length} af {nonAccepted.length}</Text>
                </View>

                {/* Current agreement */}
                {currentAgreement && (
                  <View style={styles.currentAgreementCard}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 }}>
                      {getProviderLogo(currentAgreement.provider) ? (
                        <Image source={getProviderLogo(currentAgreement.provider)!} style={styles.currentProviderLogo} />
                      ) : (
                        <View style={styles.currentProviderIcon}>
                          <Text style={styles.currentProviderInitial}>{currentAgreement.provider.charAt(0)}</Text>
                        </View>
                      )}
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <Text style={styles.currentProviderName} numberOfLines={1}>{currentAgreement.provider}</Text>
                          <View style={styles.dinAftaleBadge}>
                            <Text style={styles.dinAftaleText}>DIN AFTALE</Text>
                          </View>
                          {currentAgreement.priceConfirmed ? (
                            <View style={styles.confirmedBadge}>
                              <Check size={10} color={colors.successDark} />
                              <Text style={styles.confirmedText}>Bekræftet</Text>
                            </View>
                          ) : (
                            <View style={{ position: 'relative' }}>
                              <Pressable
                                style={styles.pendingBadge}
                                onPress={() => setPriceTooltipCat(priceTooltipCat === category ? null : category)}
                              >
                                <Clock size={10} color={colors.warning} />
                                <Text style={styles.pendingText}>Afventer</Text>
                                <Info size={9} color={colors.warning} style={{ opacity: 0.7 }} />
                              </Pressable>
                              {priceTooltipCat === category && (
                                <View style={styles.priceTooltip}>
                                  <Text style={styles.priceTooltipTitle}>Prisen er ikke bekræftet endnu</Text>
                                  <Text style={styles.priceTooltipText}>
                                    Beløbet er baseret på det du indtastede ved oprettelse. Vi afventer bekræftelse fra {currentAgreement.provider}, så vi har de præcise tal.
                                  </Text>
                                </View>
                              )}
                            </View>
                          )}
                        </View>
                        {currentAgreement.details ? <Text style={styles.currentDetails}>{currentAgreement.details}</Text> : null}
                        {/* Feature checkpoints for mobile/internet */}
                        {category === 'mobile' && currentAgreement.desiredGbDk && (
                          <View style={styles.agreementFeatures}>
                            <View style={styles.agreementFeature}><Check size={10} color={colors.success} /><Text style={styles.agreementFeatureText}>{currentAgreement.desiredGbDk} GB data</Text></View>
                            <View style={styles.agreementFeature}><Check size={10} color={colors.success} /><Text style={styles.agreementFeatureText}>Tale & SMS</Text></View>
                          </View>
                        )}
                        {category === 'internet' && currentAgreement.internetNeeds && (
                          <View style={styles.agreementFeatures}>
                            <View style={styles.agreementFeature}><Check size={10} color={colors.success} /><Text style={styles.agreementFeatureText}>
                              {currentAgreement.internetNeeds === 'premium' ? '300+ Mbit/s' : currentAgreement.internetNeeds === 'basis' ? '100 Mbit/s' : '100–300 Mbit/s'}
                            </Text></View>
                          </View>
                        )}
                      </View>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text style={styles.currentPrice}>
                        {!currentAgreement.priceConfirmed && <Text style={{ color: colors.text.muted, fontWeight: '400' }}>ca. </Text>}
                        {currentAgreement.price.toLocaleString('da-DK')} <Text style={styles.currentPriceUnit}>kr/md</Text>
                      </Text>
                    </View>
                  </View>
                )}

                {/* No cheaper offers found */}
                {currentAgreement && !hasAnyBetterOffer && (
                  <View style={styles.noBetterOfferBox}>
                    <View style={styles.noBetterOfferIcon}>
                      <Search size={16} color="#3b82f6" />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.noBetterOfferTitle}>Ingen billigere tilbud fundet endnu</Text>
                      <Text style={styles.noBetterOfferText}>
                        {`Baseret på ${nonAccepted.length} tilbud — priserne kan ændre sig i takt med markedet.`}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Top 3 quote cards */}
                {displayQuotes.map((item, qi) => (
                  <AnimatedItem key={item.id} index={staggerBase + 1 + qi}>
                    <SharedQuoteCard
                      quote={item}
                      onAccept={() => openAcceptModal(item as any)}
                      category={category}
                      savings={computeSavings(item as any)}
                      tariffs={tariffs}
                      yearlyKwh={currentAgreement?.yearlyKwh}
                      smartBadge={badgeMap.size > 0 ? (badgeMap.get(item.id) ?? null) : undefined}
                    />
                  </AnimatedItem>
                ))}

                {/* "Se alle" link */}
                {nonAccepted.length > 4 && (
                  <PressableScale accessibilityRole="link" accessibilityLabel={`Se alle ${nonAccepted.length} udbydere for ${catConfig.label}`} style={styles.seeAllLink} onPress={() => router.push(`/quotes/market?category=${category}` as any)} haptic="light">
                    <Text style={styles.seeAllText}>Se alle {nonAccepted.length} udbydere</Text>
                    <ArrowRight size={16} color={colors.primary} />
                  </PressableScale>
                )}

                {/* Energy note — under tilbuddene */}
                {isEnergy && (
                  <Text style={styles.energyNote}>
                    Estimeret månedspris inkl. spotpris, nettarif, elafgift og moms (baseret på dit forbrug: {((currentAgreement as any)?.yearlyKwh || DEFAULT_KWH_YEAR).toLocaleString('da-DK')} kWh/år). Priser med ~ er estimater baseret på aktuel spotpris.
                  </Text>
                )}
              </View>
              </AnimatedItem>
            )
          })
        ) : acceptedCategories.size > 0 ? null : (
          <AnimatedItem index={0}>
          <View style={styles.emptyState}>
            <View style={styles.emptyIcon}>
              <FileText size={32} color={colors.text.muted} />
            </View>
            <Text style={styles.emptyTitle}>
              {hunts.length === 0 ? 'Ingen tilbud at vise' : 'Vi arbejder på sagen'}
            </Text>
            <Text style={styles.emptyText}>
              {hunts.length === 0
                ? 'Start din første jagt for at lade os indhente og forhandle tilbud for dig på tværs af markedet.'
                : 'Dine jagter er aktive — vi sidder klar til at modtage og analysere de bedste tilbud til dig. De vil dukke op her.'}
            </Text>
            {hunts.length === 0 && !allCategoriesActive && (
              <PressableScale accessibilityRole="button" accessibilityLabel="Start ny jagt" style={styles.emptyCta} onPress={() => router.push('/onboarding' as any)} haptic="light">
                <Plus size={16} color={colors.text.inverse} />
                <Text style={styles.emptyCtaText}>Start ny jagt</Text>
              </PressableScale>
            )}
          </View>
          </AnimatedItem>
        )}
      </ScrollView>

      {/* Accept Modal — uses latchedAcceptQuote so it stays mounted on first open,
          ensuring BottomSheetModal ref is attached before present() is called */}
      {latchedAcceptQuote && (
        <AcceptQuoteModal
          visible={!!acceptModalQuote}
          quote={latchedAcceptQuote}
          onClose={() => setAcceptModalQuote(null)}
          userProfile={userProfile}
          tariffs={tariffs}
          yearlyKwh={currentAgreements[latchedAcceptQuote.category]?.yearlyKwh}
          hasPaymentInfo={hasPaymentInfo}
          existingPaymentReg={existingPaymentReg}
          existingPaymentAccount={existingPaymentAccount}
          onboardingPlans={(() => {
            const activeReq = quoteRequests.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
            const catPlans = activeReq?.user_data?.[latchedAcceptQuote.category]
            return Array.isArray(catPlans) ? catPlans : undefined
          })()}
          onAccept={handleAccept}
        />
      )}

      {/* Switch Status / Cancel Bottom Sheet */}
      <BottomSheetModal
        ref={cancelSwitchSheetRef}
        snapPoints={cancelConfirming ? ['48%'] : ['52%']}
        onDismiss={() => { setCancelSwitchTarget(null); setCancelConfirming(false) }}
        backdropComponent={renderSheetBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
      >
        <BottomSheetView style={styles.cancelModalContent}>
          {(() => {
            const catCfg = CATEGORY_CONFIG.find(c => c.id === cancelSwitchTarget?.category)
            const CatIcon = cancelSwitchTarget?.category === 'energy' ? Zap : cancelSwitchTarget?.category === 'mobile' ? Smartphone : Wifi
            const acceptedQ = quotes.find(q => q.status === 'accepted' && q.category === cancelSwitchTarget?.category)
            const currentAg = currentAgreements[cancelSwitchTarget?.category || '']
            let cancelSavings = acceptedQ ? Math.round(Number(acceptedQ.estimated_annual_savings) || 0) : 0
            if (!cancelSavings && acceptedQ && currentAg?.price) {
              const diff = currentAg.price - Number(acceptedQ.monthly_price)
              if (diff > 0) cancelSavings = Math.round(diff * 12)
            }

            if (cancelConfirming) {
              // Cancel confirmation view
              return (
                <>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
                    <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.errorLight, alignItems: 'center', justifyContent: 'center' }}>
                      <Info size={20} color={colors.error} />
                    </View>
                    <Text style={styles.cancelModalTitle}>
                      Annuller skift af {catCfg?.label?.toLowerCase() || cancelSwitchTarget?.category}?
                    </Text>
                  </View>
                  <Text style={styles.cancelModalText}>
                    Vi sender en annullering til {cancelSwitchTarget?.provider} og gendanner din nuværende aftale.
                  </Text>
                  {cancelSavings > 0 && (
                    <View style={styles.cancelSavingsWarning}>
                      <TrendingDown size={16} color={colors.warningDark} />
                      <Text style={styles.cancelSavingsText}>
                        Du mister en besparelse på {cancelSavings.toLocaleString('da-DK')} kr/år
                      </Text>
                    </View>
                  )}
                  <View style={styles.cancelModalButtons}>
                    <Pressable accessibilityRole="button" accessibilityLabel="Fortryd" style={styles.cancelModalNoBtn} onPress={() => setCancelConfirming(false)} disabled={cancelLoading}>
                      <Text style={styles.cancelModalNoText}>Fortryd</Text>
                    </Pressable>
                    <Pressable accessibilityRole="button" accessibilityLabel={cancelLoading ? 'Annullerer' : 'Ja, annuller skiftet'} style={[styles.cancelModalYesBtn, cancelLoading && { opacity: 0.6 }]} onPress={() => { haptics.warning(); executeCancelSwitch() }} disabled={cancelLoading}>
                      <Text style={styles.cancelModalYesText}>{cancelLoading ? 'Annullerer...' : 'Ja, annuller'}</Text>
                    </Pressable>
                  </View>
                </>
              )
            }

            // Info view (default)
            return (
              <>
                {/* Header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md }}>
                  {getProviderLogo(cancelSwitchTarget?.provider || '') ? (
                    <Image
                      source={getProviderLogo(cancelSwitchTarget?.provider || '')!}
                      style={styles.afventerCatIcon}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={[styles.afventerCatIcon, { backgroundColor: catCfg?.bg || colors.surface }]}>
                      <CatIcon size={20} color={catCfg?.color} />
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cancelModalTitle}>Skift af {catCfg?.label?.toLowerCase()}</Text>
                    <Text style={{ ...typography.caption, color: colors.text.secondary }}>Igangsat — afventer udbyder</Text>
                  </View>
                </View>

                {/* Status info rows */}
                <View style={{ gap: spacing.sm, marginBottom: spacing.md }}>
                  <View style={styles.statusInfoRow}>
                    <Text style={styles.statusInfoLabel}>Ny udbyder</Text>
                    <Text style={styles.statusInfoValue}>{cancelSwitchTarget?.provider || '—'}</Text>
                  </View>
                  {acceptedQ && (
                    <View style={styles.statusInfoRow}>
                      <Text style={styles.statusInfoLabel}>Ny pris</Text>
                      <Text style={styles.statusInfoValue}>{Math.round(Number(acceptedQ.monthly_price))} kr/md</Text>
                    </View>
                  )}
                  {currentAg && (
                    <View style={styles.statusInfoRow}>
                      <Text style={styles.statusInfoLabel}>Nuværende pris</Text>
                      <Text style={styles.statusInfoValue}>{currentAg.price} kr/md ({currentAg.provider})</Text>
                    </View>
                  )}
                  {cancelSavings > 0 && (
                    <View style={styles.statusInfoRow}>
                      <Text style={styles.statusInfoLabel}>Besparelse</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <TrendingDown size={14} color={colors.successDark} />
                        <Text style={{ ...typography.caption, color: colors.successDark, fontWeight: '600' }}>{cancelSavings.toLocaleString('da-DK')} kr/år</Text>
                      </View>
                    </View>
                  )}
                  <View style={styles.statusInfoRow}>
                    <Text style={styles.statusInfoLabel}>Status</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.warning }} />
                      <Text style={{ ...typography.caption, color: colors.warningDark, fontWeight: '500' }}>Fuldmagt sendt — afventer</Text>
                    </View>
                  </View>
                </View>

                {/* Info note */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, padding: spacing.md, backgroundColor: colors.primarySubtle, borderRadius: borderRadius.md, marginBottom: spacing.md }}>
                  <Info size={14} color={colors.primary} style={{ marginTop: 1 }} />
                  <Text style={{ ...typography.small, color: colors.primaryDark, flex: 1 }}>
                    Skiftet sker automatisk. Din nuværende udbyder håndterer opsigelsen.
                  </Text>
                </View>

                {/* Actions */}
                <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Fortryd skift"
                    style={{ flex: 1, paddingVertical: 14, backgroundColor: 'transparent', borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.md, alignItems: 'center' }}
                    onPress={() => { haptics.light(); setCancelConfirming(true) }}
                  >
                    <Text style={{ ...typography.captionBold, color: colors.error }}>Fortryd skift</Text>
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Luk"
                    style={{ flex: 1, paddingVertical: 14, backgroundColor: colors.surface, borderRadius: borderRadius.md, alignItems: 'center' }}
                    onPress={() => setCancelSwitchTarget(null)}
                  >
                    <Text style={{ ...typography.captionBold, color: colors.text.secondary }}>Luk</Text>
                  </Pressable>
                </View>
              </>
            )
          })()}
        </BottomSheetView>
      </BottomSheetModal>

      {/* Complete Hunt Bottom Sheet */}
      <BottomSheetModal
        ref={completeHuntSheetRef}
        snapPoints={['38%']}
        onDismiss={() => setCompletingHuntId(null)}
        backdropComponent={renderSheetBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
      >
        <BottomSheetView style={styles.cancelModalContent}>
          <View style={styles.completeHuntIcon}>
            <Check size={22} color={colors.success} />
          </View>
          <Text style={[styles.cancelModalTitle, { textAlign: 'center' }]}>Afslut jagt?</Text>
          <Text style={[styles.cancelModalText, { textAlign: 'center' }]}>
            Er du sikker på at du vil afslutte denne jagt? Du kan bagefter starte en ny jagt.
          </Text>
          <View style={styles.cancelModalDivider} />
          <View style={styles.cancelModalButtons}>
            <Pressable style={styles.cancelModalNoBtn} onPress={() => setCompletingHuntId(null)} disabled={completingLoading}>
              <Text style={styles.cancelModalNoText}>Annuller</Text>
            </Pressable>
            <Pressable style={styles.cancelModalCompleteBtn} onPress={() => { haptics.medium(); confirmCompleteHunt() }} disabled={completingLoading}>
              <Text style={styles.cancelModalCompleteText}>{completingLoading ? 'Afslutter...' : 'Ja, afslut jagt'}</Text>
            </Pressable>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
      </ContentContainer>
    </SafeAreaView>
  )
}

// ─── Styles ──────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    padding: spacing.md, backgroundColor: colors.background,
    borderBottomWidth: 1, borderBottomColor: colors.border,
    flexDirection: 'row', alignItems: 'center',
  },
  title: { ...typography.h2, color: colors.text.primary },
  subtitle: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  startHuntBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 14, paddingVertical: 8,
    backgroundColor: colors.primary, borderRadius: borderRadius.md,
  },
  startHuntBtnText: { ...typography.small, color: colors.text.inverse, fontWeight: '600' },
  marketLink: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 10, paddingVertical: 6,
    backgroundColor: colors.primaryLight, borderRadius: borderRadius.sm,
    borderWidth: 1, borderColor: '#bfdbfe',
  },
  marketLinkText: { ...typography.small, color: colors.primary, fontWeight: '500' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  huntBanner: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primaryLight, borderRadius: borderRadius.lg,
    borderWidth: 1, borderColor: '#bfdbfe',
  },
  huntBannerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 2 },
  huntBannerText: { ...typography.captionBold, color: colors.primaryDark },
  huntBannerStats: { fontSize: 12, color: colors.primary, fontWeight: '500' },
  unreadBadge: { backgroundColor: colors.primary, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  unreadBadgeText: { ...typography.small, color: '#fff', fontWeight: '600' },
  unreadDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary },
  huntProviderList: { borderTopWidth: 1, borderTopColor: '#e0e7ff' },
  huntProviderRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.sm, paddingVertical: 8 },
  huntProviderIcon: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  huntProviderLogo: { width: 28, height: 28, borderRadius: 8 },
  huntProviderInitial: { fontWeight: '600', fontSize: 12 },
  huntProviderName: { ...typography.small, color: colors.text.primary, flex: 1, minWidth: 0 },
  providerStatusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  providerStatusText: { ...typography.small, fontWeight: '500' },
  categorySectionHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
  },
  categorySectionIcon: {
    width: 32, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
  },
  categorySectionTitle: { ...typography.h3, color: colors.text.primary },
  categorySectionCount: { ...typography.caption, color: colors.text.muted },

  // Error banner
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginHorizontal: spacing.md, marginTop: spacing.xs, padding: spacing.sm,
    backgroundColor: '#fffbeb', borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#fde68a',
  },
  errorBannerText: { ...typography.small, color: '#92400e', flex: 1 },
  errorBannerRetry: { ...typography.small, color: colors.warningDark, fontWeight: '600' },

  // Savings summary
  savingsSummary: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.successLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.successMuted,
  },
  savingsSummaryAmount: { ...typography.h3, color: colors.successDark },
  savingsSummaryLabel: { ...typography.caption, color: colors.success },

  // Current agreement card
  currentAgreementCard: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: spacing.sm, backgroundColor: colors.surface,
    borderRadius: borderRadius.md, borderWidth: 1, borderColor: colors.border,
  },
  currentProviderIcon: {
    width: 32, height: 32, borderRadius: 16, backgroundColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  currentProviderLogo: { width: 32, height: 32, borderRadius: 10 },
  currentProviderInitial: { fontWeight: '700', fontSize: 13, color: colors.text.secondary },
  currentProviderName: { ...typography.captionBold, color: colors.text.primary },
  dinAftaleBadge: { backgroundColor: colors.border, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  dinAftaleText: { fontSize: 10, fontWeight: '600', color: colors.text.muted },
  confirmedBadge: { flexDirection: 'row', alignItems: 'center', gap: 2, backgroundColor: colors.successMuted, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  confirmedText: { fontSize: 10, fontWeight: '600', color: colors.successDark },
  pendingBadge: { flexDirection: 'row', alignItems: 'center', gap: 2, backgroundColor: colors.warningMuted, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  pendingText: { fontSize: 10, fontWeight: '600', color: colors.warning },
  priceTooltip: {
    position: 'absolute' as const, left: 0, bottom: '100%' as any, marginBottom: 6, zIndex: 50,
    width: 220, backgroundColor: '#ffffff', borderRadius: 10, padding: 10,
    borderWidth: 1, borderColor: colors.border,
    ...shadows.lg,
  },
  priceTooltipTitle: { fontSize: 11, fontWeight: '600' as const, color: colors.text.primary, marginBottom: 4 },
  priceTooltipText: { fontSize: 10, color: colors.text.secondary, lineHeight: 15 },
  currentDetails: { ...typography.small, color: colors.text.muted, marginTop: 1 },
  agreementFeatures: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 } as any,
  agreementFeature: { flexDirection: 'row', alignItems: 'center', gap: 3 } as any,
  agreementFeatureText: { ...typography.small, color: colors.text.secondary },
  currentPrice: { ...typography.bodyBold, color: colors.text.primary },
  currentPriceUnit: { ...typography.small, fontWeight: '400', color: colors.text.secondary },

  // Afventer card
  afventerCard: {
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.warningMuted,
    borderRadius: borderRadius.lg, padding: spacing.md, ...shadows.sm,
  },
  afventerCatIcon: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  afventerTitle: { ...typography.bodyBold, color: colors.text.primary },
  afventerProvider: { ...typography.caption, color: colors.text.secondary },
  afventerPrice: { ...typography.small, color: colors.text.muted },
  afventerSavingBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 2, backgroundColor: colors.successLight, borderRadius: 10,
  },
  afventerSavingText: { ...typography.small, color: colors.successDark, fontWeight: '500' },
  afventerStatusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    paddingHorizontal: 10, paddingVertical: 4,
    backgroundColor: colors.warningMuted, borderRadius: 10, borderWidth: 1, borderColor: colors.warningMuted,
  },
  afventerStatusText: { ...typography.small, color: colors.warningDark, fontWeight: '600' },
  afventerFooter: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.warningMuted,
  },
  afventerFooterCompact: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.warningMuted,
  },
  afventerInfo: { ...typography.small, color: colors.text.muted, flex: 1 },
  cancelSwitchText: { ...typography.small, color: colors.error, fontWeight: '500' },
  statusInfoRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.borderLight,
  } as any,
  statusInfoLabel: { ...typography.small, color: colors.text.muted },
  statusInfoValue: { ...typography.caption, color: colors.text.primary, fontWeight: '500' },

  // Cancel switch modal
  cancelModalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg,
  } as any,
  cancelModalContent: {
    backgroundColor: colors.background, padding: spacing.lg, paddingTop: spacing.sm,
    width: '100%',
  } as any,
  cancelModalTitle: { ...typography.h3, color: colors.text.primary, flex: 1 },
  cancelModalText: { ...typography.caption, color: colors.text.secondary, marginTop: spacing.sm, marginBottom: spacing.md, lineHeight: 20 },
  cancelModalDivider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.lg },
  completeHuntIcon: {
    width: 52, height: 52, borderRadius: 26, backgroundColor: colors.successLight,
    alignItems: 'center', justifyContent: 'center', alignSelf: 'center', marginBottom: spacing.md,
  },
  cancelModalCompleteBtn: {
    flex: 1, paddingVertical: 14, paddingHorizontal: spacing.md,
    backgroundColor: colors.primary, borderRadius: borderRadius.md, alignItems: 'center',
  },
  cancelModalCompleteText: { ...typography.captionBold, color: colors.text.inverse },
  cancelModalButtons: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'center' } as any,
  cancelModalNoBtn: {
    flex: 1, paddingVertical: 14, paddingHorizontal: spacing.md,
    backgroundColor: 'transparent', borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.md, alignItems: 'center',
  },
  cancelModalNoText: { ...typography.captionBold, color: colors.text.secondary },
  cancelModalYesBtn: {
    flex: 1, paddingVertical: 14, paddingHorizontal: spacing.md,
    backgroundColor: colors.error, borderRadius: borderRadius.md, alignItems: 'center',
  },
  cancelModalYesText: { ...typography.captionBold, color: colors.text.inverse },
  cancelSavingsWarning: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: colors.warningLight, borderWidth: 1, borderColor: colors.warningMuted,
    borderRadius: borderRadius.md, padding: spacing.sm, marginBottom: spacing.md,
  } as any,
  cancelSavingsText: { ...typography.caption, color: colors.warningDark, fontWeight: '500', flex: 1 },

  // Complete hunt
  showAwaitingBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#e0e7ff',
  } as any,
  showAwaitingText: { ...typography.small, color: colors.text.muted, fontWeight: '500' },
  completeHuntFooter: {
    paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    borderTopWidth: 1, borderTopColor: '#e0e7ff',
    flexDirection: 'row', justifyContent: 'flex-end',
    backgroundColor: 'rgba(249,250,251,0.5)',
  } as any,
  completeHuntBtn: {
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    borderRadius: borderRadius.sm,
  },
  completeHuntText: { ...typography.small, color: colors.error, fontWeight: '500' },

  // Se alle link
  seeAllLink: {
    flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: spacing.sm, paddingVertical: spacing.xs,
  },
  seeAllText: { ...typography.caption, color: colors.primary, fontWeight: '600' },

  // Empty
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, gap: spacing.sm },
  emptyIcon: {
    width: 64, height: 64, borderRadius: 32, backgroundColor: colors.borderLight,
    alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm,
  },
  emptyTitle: { ...typography.h3, color: colors.text.primary },
  emptyText: { ...typography.caption, color: colors.text.secondary, textAlign: 'center', maxWidth: 280 },
  emptyCta: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.md,
    paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    backgroundColor: colors.primary, borderRadius: borderRadius.md,
  },
  emptyCtaText: { ...typography.captionBold, color: colors.text.inverse },

  // No better offer info box
  noBetterOfferBox: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.sm, backgroundColor: '#eff6ff',
    borderRadius: borderRadius.md, borderWidth: 1, borderColor: '#bfdbfe',
  },
  noBetterOfferIcon: {
    width: 28, height: 28, borderRadius: 14, backgroundColor: '#dbeafe',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  noBetterOfferTitle: { ...typography.captionBold, color: '#1e40af' },
  noBetterOfferText: { ...typography.small, color: '#3b82f6', marginTop: 1 },

  // Energy note
  energyNote: { ...typography.small, color: colors.text.muted, fontSize: 11, paddingHorizontal: 2 },

  // Switch compare card (Før → Efter)
  switchCompareCard: {
    backgroundColor: '#fefce8',
    borderRadius: borderRadius.lg,
    borderWidth: 1,
    borderColor: '#fde68a',
    padding: spacing.md,
    gap: spacing.sm,
  },
  switchCompareHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  switchCompareTitle: {
    ...typography.captionBold,
    color: colors.text.primary,
  },
  switchCompareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  switchCompareCol: {
    flex: 1,
  },
  switchCompareArrow: {
    width: 32,
    alignItems: 'center',
  },
  switchCompareLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.text.muted,
    letterSpacing: 0.5,
  },
  switchCompareProvider: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
  switchCompareOldPrice: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text.muted,
    marginTop: 2,
    textDecorationLine: 'line-through',
  },
  switchCompareNewPrice: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.successDark,
    marginTop: 2,
  },
  switchCompareFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },

  // Category tab bar
  categoryTabBar: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: spacing.sm,
  },
  categoryTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: borderRadius.full,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  categoryTabText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.primary,
  },
  categoryTabBadge: {
    backgroundColor: colors.primaryLight,
    paddingHorizontal: 5,
    paddingVertical: 0,
    borderRadius: 8,
    minWidth: 18,
    alignItems: 'center',
  },
  categoryTabBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.primary,
  },
})
